/**
 * MARK:- Zone verdict (#2899)
 * One zone-suitability contract for the storefront. nh-hub's
 * src/lib/catalog-facts/zone-suitability.ts implements the same rules and runs the
 * same fixture table (qa/zone-verdict-fixtures.mjs here); change both together.
 *
 * Field precedence: custom.growing_zone_range, else custom.growing_zones as min..max.
 * Halves are ignored. Rules, first wins:
 *   1. product type "Potted Houseplants" or tag type_houseplant -> not_judged
 *   2. zone range mentions patio, indoor, houseplant or annual -> not_judged
 *   3. customer zone has no leading integer 1..13 -> unknown
 *   4. no numeric range and no numeric list -> unknown
 *   5. outside min..max -> out_of_zone, else in_zone
 *
 * Shopper zone (currentZone): the latest persisted cart attribute (cart:attribute-updated),
 * else the server-rendered cart attribute customer_growing_zone, else localStorage.
 * Consumers re-judge on the 'nhzone:changed' document event.
 *
 * window.NHZone in the browser; module.exports in node (qa/zone-verdict-fixtures.mjs).
 */
(function (root) {
  'use strict';

  const MIN_ZONE = 1;
  const MAX_ZONE = 13;
  const NOT_JUDGED_RANGE_RE = /patio|indoor|houseplant|annual/i;
  const RANGE_RE = /^(\d{1,2})(a|b)?-(\d{1,2})(a|b)?$/;
  const SINGLE_RE = /^(\d{1,2})(a|b)?$/;

  const PROPERTY_KEY = 'Growing zone';
  const LEGACY_KEY = 'Growing Zone Notice';
  const MARKER_KEY = '_zone_checked';

  function zoneNumber(raw, re) {
    if (raw === null || raw === undefined) return null;
    const m = re.exec(String(raw));
    if (!m) return null;
    const n = Number(m[1]);
    return n >= MIN_ZONE && n <= MAX_ZONE ? n : null;
  }

  // MARK:- Same accepted shape as the Hub's parseZoneRange: "5-9", "5b-9a", "7"
  function parseRange(raw) {
    if (!raw) return null;
    const normalized = String(raw).trim()
      .replace(/[–—]/g, '-')
      .replace(/\s+/g, ' ')
      .replace(/\s*-\s*/g, '-')
      .toLowerCase();
    if (!normalized) return null;
    let min;
    let max;
    let m = RANGE_RE.exec(normalized);
    if (m) {
      min = Number(m[1]);
      max = Number(m[3]);
    } else if ((m = SINGLE_RE.exec(normalized))) {
      min = Number(m[1]);
      max = min;
    } else {
      return null;
    }
    if (min < MIN_ZONE || max > MAX_ZONE || max < MIN_ZONE || min > MAX_ZONE || min > max) return null;
    return { min, max };
  }

  function plantBounds(zoneRange, zoneList) {
    const parsed = parseRange(zoneRange);
    if (parsed) return parsed;
    // MARK:- Leading integer only ("5a" -> 5), the same anchor as snippets/custom-zone-verdict.liquid
    const nums = (Array.isArray(zoneList) ? zoneList : [])
      .map((z) => zoneNumber(z, /^\s*(\d{1,2})/))
      .filter((n) => n !== null);
    if (nums.length === 0) return null;
    return { min: Math.min(...nums), max: Math.max(...nums) };
  }

  /**
   * @param {{zoneRange?: string, zoneList?: string[], productType?: string, tags?: string[]}} facts
   * @param {string|null} customerZone - "5a", "5" or null
   * @returns {{verdict: string, min: number|null, max: number|null, customerZoneNumber: number|null, reason: string}}
   */
  function judge(facts, customerZone) {
    const f = facts || {};
    const notJudged = (reason) => ({ verdict: 'not_judged', min: null, max: null, customerZoneNumber: null, reason });
    const tags = (Array.isArray(f.tags) ? f.tags : []).map((t) => String(t).trim().toLowerCase());
    if (f.productType === 'Potted Houseplants' || tags.includes('type_houseplant')) return notJudged('houseplant');
    if (f.zoneRange && NOT_JUDGED_RANGE_RE.test(f.zoneRange)) return notJudged('special_zone_semantics');

    const bounds = plantBounds(f.zoneRange, f.zoneList);
    const cz = customerZone ? zoneNumber(customerZone, /^\s*(\d{1,2})/) : null;
    const min = bounds ? bounds.min : null;
    const max = bounds ? bounds.max : null;
    if (cz === null) return { verdict: 'unknown', min, max, customerZoneNumber: null, reason: 'customer_zone_unknown' };
    if (!bounds) return { verdict: 'unknown', min, max, customerZoneNumber: cz, reason: 'plant_zone_unknown' };
    return cz < bounds.min || cz > bounds.max
      ? { verdict: 'out_of_zone', min, max, customerZoneNumber: cz, reason: 'outside_range' }
      : { verdict: 'in_zone', min, max, customerZoneNumber: cz, reason: 'inside_range' };
  }

  // MARK:- Cold-side miss: the shopper's zone is below the plant's minimum (pot-and-overwinter copy applies)
  function coldSide(result) {
    return Boolean(result) && result.verdict === 'out_of_zone'
      && result.customerZoneNumber !== null && result.min !== null && result.customerZoneNumber < result.min;
  }

  // MARK:- Storefront-only Plus predicate (the Hub never calls it)
  function plusEligible(line) {
    return Boolean(line) && line.warrantable === true && line.verdict !== 'out_of_zone';
  }

  // MARK:- Cart pill for a warrantable line when Plus is in the cart; mirrors plusEligible so the
  // pill never says covered on a line the Plus price excludes (snippets/cart-items.liquid renders the same)
  function warrantyPill(verdict) {
    return plusEligible({ warrantable: true, verdict })
      ? { state: 'covered', text: 'Covered by warranty' }
      : { state: 'out_of_zone', text: 'Out of zone, not covered' };
  }

  // MARK:- Leading integer of a shopper zone, as shown in copy ("5a" -> "5")
  function zoneLabel(zone) {
    const n = zoneNumber(zone, /^\s*(\d{1,2})/);
    return n === null ? '' : String(n);
  }

  function propertyValue(zone) {
    return `Outside zone ${zoneLabel(zone)}, not guaranteed`;
  }

  /**
   * Full property set a line should carry. Shopify cannot reset properties to {} once set,
   * so a cleared warning becomes the private marker; `_` keys are kept.
   */
  function desiredProperties(existing, verdict, zone) {
    const out = {};
    Object.keys(existing || {}).forEach((key) => {
      if (key === PROPERTY_KEY || key === LEGACY_KEY || key === MARKER_KEY) return;
      out[key] = existing[key];
    });
    if (verdict === 'out_of_zone') out[PROPERTY_KEY] = propertyValue(zone);
    if (Object.keys(out).length === 0) out[MARKER_KEY] = '1';
    return out;
  }

  function stableJson(props) {
    const obj = props || {};
    return JSON.stringify(Object.keys(obj).filter((k) => k !== MARKER_KEY).sort().map((k) => [k, String(obj[k])]));
  }

  // MARK:- Full-set compare; the marker alone carries no information, so marker-only is in sync
  function needsWrite(existing, desired) {
    return stableJson(existing) !== stableJson(desired);
  }

  /**
   * Plus price in dollars ("12.34"), from a /cart.js payload and a verdict map keyed by line key.
   * @param {object} cart - /cart.js payload
   * @param {Object<string, {warrantable: boolean, verdict: string}>} verdicts
   * @param {number} pct - warranty percentage (e.g. 17.32)
   */
  function plusPrice(cart, verdicts, pct) {
    let total = 0;
    ((cart && cart.items) || []).forEach((item) => {
      if (plusEligible(verdicts[item.key])) total += item.original_line_price;
    });
    return ((total * (Number(pct) || 0)) / 100 / 100).toFixed(2);
  }

  const NHZone = {
    PROPERTY_KEY,
    LEGACY_KEY,
    MARKER_KEY,
    judge,
    coldSide,
    plusEligible,
    warrantyPill,
    zoneLabel,
    propertyValue,
    desiredProperties,
    needsWrite,
    plusPrice,
    currentZone: () => null
  };

  if (typeof module !== 'undefined' && module.exports) {
    module.exports = NHZone;
    return;
  }

  // MARK:- Browser: shopper zone resolver and change events
  let persistedZone = null;
  let fallbackTimer = null;

  function serverZone() {
    const z = root.NHZoneServer && root.NHZoneServer.cartZone;
    return z ? String(z) : null;
  }

  function storedZone() {
    try {
      return root.localStorage.getItem('growing_zone');
    } catch (e) {
      return null;
    }
  }

  NHZone.currentZone = function currentZone() {
    return persistedZone || serverZone() || storedZone() || null;
  };

  let lastZone = NHZone.currentZone();

  function announce() {
    const zone = NHZone.currentZone();
    if (zone === lastZone) return;
    lastZone = zone;
    document.dispatchEvent(new CustomEvent('nhzone:changed', { detail: { zone } }));
  }

  // MARK:- Persisted zone: grow-zone-finder.js syncZoneToCartAttribute fires this after /cart/update.js
  document.addEventListener('cart:attribute-updated', (event) => {
    const detail = event.detail || {};
    if (detail.attributeKey !== 'customer_growing_zone' || !detail.attributeValue) return;
    clearTimeout(fallbackTimer);
    persistedZone = String(detail.attributeValue);
    announce();
  });

  // MARK:- zone:updated precedes the attribute write; use it only if the write never lands
  document.addEventListener('zone:updated', (event) => {
    const zone = event.detail && event.detail.zoneNumber;
    if (!zone) return;
    clearTimeout(fallbackTimer);
    fallbackTimer = setTimeout(() => {
      persistedZone = String(zone);
      announce();
    }, 4000);
  });

  // MARK:- Header callout shows the zone being persisted (it was server-rendered from the old attribute)
  function showHeaderZone(zone, zip) {
    ['', '-mobile'].forEach((suffix) => {
      const zoneEl = document.getElementById(`ZoneNumber${suffix}`);
      const zipEl = document.getElementById(`ZipCode${suffix}`);
      const details = document.getElementById(`ZoneDetailsContainer${suffix}`);
      if (zoneEl) zoneEl.textContent = zone;
      if (zipEl && zip) zipEl.textContent = zip;
      if (details) details.dataset.cartZone = zone;
    });
  }

  // MARK:- Reconcile: a ZIP saved on this device but never persisted to the cart attribute,
  // either missing (new cart; the header inline sync runs before grow-zone-finder.js defines
  // syncZoneToCartAttribute) or different from it
  function reconcileStoredZone() {
    const stored = storedZone();
    const server = serverZone();
    if (!stored || !zoneLabel(stored)) return;
    if (server && zoneLabel(stored) === zoneLabel(server)) return;
    if (typeof root.syncZoneToCartAttribute !== 'function') return;
    let zip = '';
    let state = null;
    try {
      zip = root.localStorage.getItem('growing_zipcode') || '';
      state = root.localStorage.getItem('growing_state');
    } catch (e) {
      // Storage read failed above too; nothing to reconcile.
    }
    if (server) showHeaderZone(stored, zip);
    root.syncZoneToCartAttribute(stored, zip, state);
  }

  // MARK:- Desktop: the grow-zone panel sits in the header's stacking context (z-index 30), under the
  // open cart drawer (40). A zone link inside the drawer closes it first (capture phase, before the
  // link's inline toggleGrowZoneDrawer) and reopens it when the panel closes (submit, X, Esc or
  // click-outside). Mobile portals the panel to <body> as a bottom sheet over the drawer, so it is left alone.
  const DRAWER_ZONE_LINKS = '.cart-item__zone-badge-link, .gg-zone-details__link, .cart-restriction-notice__zone-link, .cart-restriction-notice__action, .custom-account-panel__zone-link';
  let reopenDrawer = false;
  document.addEventListener('click', (event) => {
    const link = event.target && event.target.closest && event.target.closest(DRAWER_ZONE_LINKS);
    if (!link || !link.closest('cart-drawer') || root.innerWidth <= 768) return;
    const panel = document.getElementById('FindGrowZoneDrawer');
    if (!panel || panel.classList.contains('open')) return;
    reopenDrawer = true;
    document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:close'));
    const watch = new MutationObserver(() => {
      if (panel.classList.contains('open') || !reopenDrawer) return;
      reopenDrawer = false;
      watch.disconnect();
      // Next task, so an Esc that closed the panel does not also reach the drawer's own Esc handler.
      setTimeout(() => document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:open')), 300);
    });
    watch.observe(panel, { attributes: true, attributeFilter: ['class'] });
  }, true);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', reconcileStoredZone);
  } else {
    reconcileStoredZone();
  }

  root.NHZone = NHZone;
})(typeof window !== 'undefined' ? window : globalThis);
